import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Bolt, Link as LinkIcon } from '@mui/icons-material';
import { PgPage } from '../common/PgPage';
import { useReloadKey } from '../common/helpers';
import { ChargeInvoiceDialog } from './ChargeInvoiceDialog';
import { PaymentLinkDialog } from './PaymentLinkDialog';
import { PaymentsList } from './PaymentsList';
import { RefundsList } from './RefundsList';

type TPaymentsView = 'payments' | 'refunds';

/**
 * Payments (03 §4.8–4.9): list with filters and the payment card; refunds of all payments; the
 * payment link generator and the manual charge of an invoice.
 */
export const Payments: FC = () => {
	const navigate = useNavigate();
	const [view, setView] = useState<TPaymentsView>('payments');
	const [linkOpen, setLinkOpen] = useState(false);
	const [chargeOpen, setChargeOpen] = useState(false);
	const [reloadKey, reload] = useReloadKey();

	const handleViewChange = (_event: unknown, next: TPaymentsView | null) => {
		if (!next || next === view) return;
		// The two lists share the URL query (page, sort, filters): start the other one clean.
		navigate({ search: '' }, { replace: true });
		setView(next);
	};

	return (
		<PgPage
			title="Payments"
			actions={
				<>
					<Button
						size="small"
						variant="outlined"
						startIcon={<LinkIcon />}
						onClick={() => setLinkOpen(true)}
					>
						Payment link
					</Button>
					<Button
						size="small"
						variant="contained"
						startIcon={<Bolt />}
						onClick={() => setChargeOpen(true)}
					>
						Charge invoice
					</Button>
				</>
			}
		>
			<Box sx={{ mb: 1 }}>
				<ToggleButtonGroup size="small" exclusive value={view} onChange={handleViewChange}>
					<ToggleButton value="payments">Payments</ToggleButton>
					<ToggleButton value="refunds">Refunds</ToggleButton>
				</ToggleButtonGroup>
			</Box>
			{view === 'payments' ? (
				<PaymentsList reloadKey={reloadKey} />
			) : (
				<RefundsList reloadKey={reloadKey} />
			)}
			<PaymentLinkDialog open={linkOpen} onClose={() => setLinkOpen(false)} />
			<ChargeInvoiceDialog open={chargeOpen} onClose={() => setChargeOpen(false)} onDone={reload} />
		</PgPage>
	);
};
